"use client";

import Link from "next/link";
import Image from "next/image";
import {
  ChevronRight,
  FileText,
  HelpCircle,
  ListChecks,
  PenSquare,
  PlayCircle,
  Lock,
  type LucideIcon,
} from "lucide-react";
import { Accordion } from "@/components/ui/Accordion";
import { RichHtml } from "@/components/ui/RichHtml";
import { cn } from "@/components/ui/cn";
import { useDict } from "@/components/i18n/DictProvider";
import { type Dict, type Lang } from "@/lib/i18n";
import { formatItems, formatSections, stripTags } from "@/lib/format";
import { lessonFree, lessonTitle, publishedLessons, publishedSections } from "./lessonNav";
import type { Lesson, ModuleDetail } from "@/lib/lecturehub/types";
import { OrganizationChips } from "./OrganizationChips";
import { InstructorCards } from "./InstructorCards";

const refIcon: Record<string, LucideIcon> = {
  lecture: PlayCircle,
  quiz: ListChecks,
  article: FileText,
  assignment: PenSquare,
};

function refLabel(lt: Dict["learn"]): Record<string, string> {
  return {
    lecture: lt.typeLecture,
    quiz: lt.typeQuiz,
    article: lt.typeArticle,
    assignment: lt.typeAssignment,
  };
}

function LessonThumb({
  Icon,
  locked,
  poster,
}: {
  Icon: LucideIcon;
  locked: boolean;
  /** Poster image URL when the lesson has one — LessonRow resolves which image.
   *  A lesson with no image at all falls back to the icon tile. */
  poster?: string | null;
}) {
  return (
    <span
      className={cn(
        "lesson-row__thumb shrink-0 relative aspect-[16/9] w-32 md:w-40 rounded-lg overflow-hidden",
        locked ? "bg-muted text-muted-foreground" : "bg-primary/[0.06] text-primary",
      )}
    >
      {poster ? (
        // alt="" — the title is the row's next sibling inside the same link.
        <Image
          src={poster}
          alt=""
          fill
          sizes="128px"
          className={cn("object-cover", locked ? "opacity-60" : "")}
        />
      ) : (
        <span className="absolute inset-0 grid place-items-center">
          <Icon className="size-6" strokeWidth={1.75} aria-hidden />
        </span>
      )}
    </span>
  );
}

function LessonRow({
  lesson,
  canView,
  index,
  learnBase,
}: {
  lesson: Lesson;
  canView: boolean;
  index: number;
  learnBase: string;
}) {
  const t = useDict();
  const free = lessonFree(lesson);
  const locked = !canView && !free;
  const href = `${learnBase}/${lesson.id}`;
  const Icon = refIcon[lesson.refType] ?? PlayCircle;
  const typeLabel = refLabel(t.learn)[lesson.refType] ?? lesson.refType;
  // Thumbnail resolution, in order of how much the image is ABOUT this lesson.
  // Every lesson subtype on the live schema carries its own `image`, so the
  // lesson's own picture always wins; the older chain skipped straight to the
  // instructor's face because a comment here claimed Lecture had no image or
  // video field, which was never true of the live schema (found by the
  // field-level coverage walk, 2026-09-13 — decisions.md D33).
  // Only one subtype is non-null per lesson (refType dispatch), so a flat chain
  // cannot pick another lesson type's picture.
  //   1. the lesson's own image      lecture / quiz / article / assignment
  //   2. the lecture's video poster  a lecture with no image of its own
  //   3. the first instructor        talking-head visual, as before
  //   4. none                        the icon tile
  const lectureInstructor = lesson.lecture?.instructors?.[0];
  const poster =
    lesson.lecture?.image?.uri ??
    lesson.quiz?.image?.uri ??
    lesson.article?.image?.uri ??
    lesson.assignment?.image?.uri ??
    lesson.lecture?.video?.image?.uri ??
    lectureInstructor?.thumb?.uri ??
    lectureInstructor?.image?.uri ??
    null;

  const inner = (
    <>
      <span className="lesson-row__index text-xs tabular-nums font-bold text-muted-foreground w-6 shrink-0 text-right">
        {String(index + 1).padStart(2, "0")}
      </span>

      <LessonThumb Icon={Icon} locked={locked} poster={poster} />

      <div className="lesson-row__body min-w-0 flex-1">
        <p
          className={cn(
            "lesson-row__title font-display font-bold text-base leading-snug line-clamp-2",
            locked ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {lessonTitle(lesson)}
        </p>
        <p className="lesson-row__meta mt-1 flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span className="uppercase tracking-wider font-bold">{typeLabel}</span>
          {free ? (
            <>
              <span aria-hidden className="text-foreground/25">
                ·
              </span>
              <span className="font-bold uppercase tracking-wider text-success-foreground">
                {t.detail.free}
              </span>
            </>
          ) : null}
        </p>
      </div>

      <span className="lesson-row__action shrink-0 self-center text-muted-foreground">
        {locked ? (
          <Lock className="size-4" aria-hidden />
        ) : (
          <ChevronRight className="size-5" aria-hidden />
        )}
      </span>
    </>
  );

  const rowBase = "lesson-row flex items-start gap-4 py-4 border-b border-border last:border-0";

  return (
    <li className={locked ? "opacity-70" : ""}>
      {locked ? (
        <div className={rowBase} aria-disabled="true">
          {inner}
        </div>
      ) : (
        <Link
          href={href}
          className={cn(
            "group/lesson",
            rowBase,
            "px-3 -mx-3 rounded-xl hover:bg-muted/50 transition-colors no-underline",
          )}
        >
          {inner}
        </Link>
      )}
    </li>
  );
}

function countLessons(course: ModuleDetail["course"]): number {
  if (!course) return 0;
  if (course.childrenType === "sections") {
    return publishedSections(course).reduce(
      (sum, s) => sum + publishedLessons(s.lessons).length,
      0,
    );
  }
  return publishedLessons(course.lessons).length;
}

export function CourseView({
  module: m,
  lang,
  courseHref,
}: {
  module: ModuleDetail;
  lang: Lang;
  courseHref: string;
}) {
  const t = useDict();
  const course = m.course;
  const sections = publishedSections(course);
  const flatLessons = publishedLessons(course?.lessons);
  const canView = m.canView !== false;
  const lessonCount = countLessons(course);
  const isSectioned = course?.childrenType === "sections";
  // Lesson rows link into the player at /learn/[id]/[slug]/[lessonId],
  // not the old /courses/[id]/[slug]/[lessonId] (deleted route).
  const learnBase = courseHref.replace("/courses/", "/learn/");

  const allFaqs = course?.courseFaq ? parseFaqs(course.courseFaq) : [];

  const aboutBody = course?.courseIntro ?? m.description ?? "";
  const hasAboutContent = !!(m.abstract || aboutBody);

  return (
    <article className="grid gap-6 min-w-0">
      {hasAboutContent ? (
        <SectionCard title={t.detail.aboutCourse}>
          {m.abstract ? (
            // Abstract acts as the editorial deck for the About block: a gold
            // accent rule on the left so it reads as a lead distinct from the
            // longer description body below it. Sanitized via RichHtml (backend
            // HTML is untrusted) — same wrapper as the description below.
            <RichHtml
              html={m.abstract}
              className="relative pl-5 border-l-2 border-accent/70 text-[16px] md:text-[18px] leading-relaxed text-foreground/85 mb-5"
            />
          ) : null}
          {aboutBody ? <RichHtml html={aboutBody} /> : null}
        </SectionCard>
      ) : null}

      <section className="course-section rounded-card p-6 md:p-8 bg-card border border-border/60">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-5">
          <h2 className="!text-[20px] md:!text-[22px] !font-semibold !leading-tight !text-foreground !mb-0">
            {t.detail.curriculum}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isSectioned ? (
              <>
                {formatSections(sections.length, lang, t.units)}
                {" · "}
              </>
            ) : null}
            {formatItems(lessonCount, lang, t.units)}
          </p>
        </div>

        {isSectioned ? (
          sections.length === 0 ? (
            <EmptyCurriculum />
          ) : (
            <div className="grid gap-3">
              {sections.map((s, i) => (
                <Accordion
                  key={s.id}
                  defaultOpen={i === 0}
                  title={
                    // Two literal class strings, not cn(): with no image the
                    // attribute is byte-for-byte the one that shipped before, so
                    // the case every portal is actually in cannot regress. No
                    // portal authors section images today (52 sections, 0 images
                    // across all four, checked 2026-09-13).
                    <span
                      className={
                        s.image?.uri ? "flex items-center gap-2" : "flex items-baseline gap-2"
                      }
                    >
                      {s.image?.uri ? (
                        // alt="" — the section name is the next sibling inside
                        // the same heading, same rule as LessonThumb.
                        <span className="course-section__thumb shrink-0 relative aspect-[16/9] w-16 rounded overflow-hidden bg-primary/[0.06]">
                          <Image
                            src={s.image.uri}
                            alt=""
                            fill
                            sizes="64px"
                            className="object-cover"
                          />
                        </span>
                      ) : null}
                      <span className="text-muted-foreground font-normal text-sm">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {s.name ?? "Section"}
                    </span>
                  }
                  meta={
                    <span>{formatItems(publishedLessons(s.lessons).length, lang, t.units)}</span>
                  }
                >
                  {publishedLessons(s.lessons).length ? (
                    <ul>
                      {publishedLessons(s.lessons).map((l, idx) => (
                        <LessonRow
                          key={l.id}
                          lesson={l}
                          canView={canView}
                          index={idx}
                          learnBase={learnBase}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground text-sm py-2">{t.detail.sectionEmpty}</p>
                  )}
                </Accordion>
              ))}
            </div>
          )
        ) : flatLessons.length === 0 ? (
          <EmptyCurriculum />
        ) : (
          <ul>
            {flatLessons.map((l, idx) => (
              <LessonRow
                key={l.id}
                lesson={l}
                canView={canView}
                index={idx}
                learnBase={learnBase}
              />
            ))}
          </ul>
        )}
      </section>

      {course?.coursePrerequisites ? (
        <SectionCard title={t.detail.prerequisites}>
          <RichHtml html={course.coursePrerequisites} />
        </SectionCard>
      ) : null}

      {course?.courseSyllabus ? (
        <SectionCard title={t.detail.syllabus}>
          <RichHtml html={course.courseSyllabus} />
        </SectionCard>
      ) : null}

      {course?.instructors && course.instructors.length > 0 ? (
        <SectionCard
          title={course.instructors.length === 1 ? t.detail.instructorOne : t.detail.instructorMany}
        >
          <InstructorCards instructors={course.instructors} />
        </SectionCard>
      ) : null}

      {allFaqs.length > 0 ? (
        <SectionCard
          title={t.detail.faq}
          icon={<HelpCircle className="size-5 text-primary" aria-hidden />}
        >
          <div className="grid gap-3">
            {allFaqs.map((qa, i) => (
              <Accordion key={i} title={qa.q}>
                <RichHtml html={qa.a} />
              </Accordion>
            ))}
          </div>
        </SectionCard>
      ) : course?.courseFaq ? (
        <SectionCard
          title={t.detail.faq}
          icon={<HelpCircle className="size-5 text-primary" aria-hidden />}
        >
          <RichHtml html={course.courseFaq} />
        </SectionCard>
      ) : null}

      {course?.courseMaterials ? (
        <SectionCard title={t.detail.materials}>
          <RichHtml html={course.courseMaterials} />
        </SectionCard>
      ) : null}

      {course?.courseNews ? (
        <SectionCard title={t.detail.whatsNew} accent>
          <RichHtml html={course.courseNews} />
        </SectionCard>
      ) : null}

      <OrganizationChips organizations={m.organizations} t={t} lang={lang} />
    </article>
  );
}

function SectionCard({
  title,
  icon,
  accent = false,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "course-section rounded-card p-6 md:p-8",
        accent ? "bg-accent/[0.06] border border-accent/25" : "bg-card border border-border/60",
      )}
    >
      <header className="flex items-center gap-3 mb-5">
        {icon ? (
          <span className="grid place-items-center size-9 rounded-xl bg-accent/[0.08] text-accent">
            {icon}
          </span>
        ) : null}
        <h2 className="!text-[20px] md:!text-[22px] !font-semibold !leading-tight !text-foreground !mb-0">
          {title}
        </h2>
      </header>
      {children}
    </section>
  );
}

function EmptyCurriculum() {
  const t = useDict();
  return (
    <div className="rounded-card border border-border/60 p-6">
      <p className="text-muted-foreground">{t.detail.curriculumUnpublished}</p>
    </div>
  );
}

function parseFaqs(html: string): { q: string; a: string }[] {
  if (!html) return [];
  const parts = html.split(/(?=<h[234][^>]*>)/i);
  const out: { q: string; a: string }[] = [];
  for (const p of parts) {
    const m = p.match(/^<h[234][^>]*>([\s\S]*?)<\/h[234]>([\s\S]*)$/i);
    if (m && m[1] != null && m[2] != null) {
      const q = stripTags(m[1]).trim();
      const a = m[2].trim();
      if (q && a) out.push({ q, a });
    }
  }
  return out;
}
